<template>
  <div class="multiselect-tag-group">
    <multi-select
      v-bind="{ ...$props, ...$attrs }"
      :label="label"
      :mode="slim ? 'tags' : 'multiple'"
      :searchable="true"
      :object="object ?? true"
      :close-on-select="false"
      :can-clear="canClear"
      :multiple-label="() => ''"
      :options="asyncOptions ?? arrayOptions ?? options"
      :placeholder="placeholder"
      :loading="loading"
      :variant="slim ? 'narrow' : 'default'"
      :class="slim ? 'multiselect-red' : '!w-full'"
      :classes="slim && {
        dropdown: 'multiselect-dropdown !z-[105]',
        tag: 'multiselect-tag !pl-5 !py-0',
        tagRemove: 'multiselect-tag-remove border-l !ml-4',
        tags: 'multiselect-tags !pl-0 gap-x-3 gap-y-2.5',
        clear: 'multiselect-clear !pl-5',
        tagsSearchWrapper: 'multiselect-tags-search-wrapper min-w-[5rem]',
      }"
    />
    <multi-select
      v-if="!slim"
      v-bind="{ ...$props, ...$attrs }"
      mode="tags"
      :multiple-label="() => ''"
      :object="object ?? true"
      :show-options="false"
      label=""
      :searchable="false"
      :options="arrayOptions ?? options"
      :placeholder="$t('nothingSelected')"
      class="mt-2 !w-full multiselect-red !h-auto"
      :classes="{
        tag: 'multiselect-tag !pl-5 !py-0',
        tagRemove: 'multiselect-tag-remove border-l !ml-4',
        tags: 'multiselect-tags !pl-0 gap-x-3 gap-y-2.5',
        clear: 'multiselect-clear !pl-5',
      }"
    />
  </div>
</template>

<script lang="ts" setup>
import type {Variant} from '@components/utils/inputs/Field.vue';
import MultiSelect from '@utils/Multiselect.vue';
import {ref} from 'vue';

export interface Option {
  labelProp?: string;
  variant?: Variant;
  label: string;
  value: string;
}

export type OptionsQueryFn = () => Promise<Option[]>;

interface Props {
  label: string;
  placeholder?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modelValue: any;
  options: Option[] | OptionsQueryFn;
  object?: undefined | boolean;
  async?: boolean;
  slim?: boolean;
  canClear?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  object: undefined,
});

const arrayOptions = ref<object[] | undefined>(undefined);

let optionsRes;

// if options is a function
if (props.options !== null &&
  typeof props.options === 'function' && !props.async) {
  optionsRes = props.options();
}

const loading = ref(false);

const loadPromise = async (promise: Promise<Option[]>) => {
  loading.value = true;
  try {
    arrayOptions.value = await promise;
  } finally {
    loading.value = false;
  }
};

// If optionsRes is a Promise
if (typeof optionsRes === 'object' &&
typeof optionsRes.then === 'function' && !props.async) {
  arrayOptions.value = [];
  loadPromise(optionsRes);
}

let asyncOptions;

// If async
if (props.async && typeof props.options === 'function') {
  asyncOptions = async (query: string, select: any) => {
    const options = await props.options(query, select);
    arrayOptions.value = [...(arrayOptions.value ?? []), ...options];
    return options;
  };
}
</script>

<style lang="scss" scoped>
.multiselect-red {
  --ms-tag-bg: var(--color-primary);
  --ms-tag-color: white;
  --ms-tag-font-size: 1rem;
  --ms-tag-font-weight: 400;
  --ms-tag-remove-radius: 0px 4px 4px 0px;
  --ms-tag-remove-py: 0.4rem;
  --ms-tag-remove-px: 0.5rem;
  --ms-tag-my: 0;
  --ms-tag-mx: 0;
}
</style>
